import React, { useState, useEffect } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import api from 'api/api'
import styles from './wallet-history.module.scss'
import { upvalueDisplayAmount, upvalueMatchesWalletTab } from 'lib/upvaluePayment'
import cn from 'classnames'
import SatsIcon from '../shared/SatsIcon'
import { UserStream } from 'components/filters/FilterIcon'
import useStreamFilters from 'components/filters/useStreamFilters'
import ErrorBoundary from 'components/shared/ErrorBoundary'
import { motion, AnimatePresence } from 'framer-motion'
import WithTooltip from 'components/shared/WithTooltip'
import BsvTransactionStatusIcon from 'components/shared/BsvTransactionStatusIcon'
import useStore from 'state/knovStore'
import { X, RotateCw } from 'lucide-react'
import { getBsvTransactionState, BSV_TX_STATES } from 'utils/bsvTransactionStatus'
import useBsvDisplay from 'components/shared/useBsvDisplay'
import { getClientShare } from 'lib/bsv-signing'
import { treechatSignsWithoutLocalKey } from 'lib/clientSigningFeature'
import {
    NO_LOCAL_SIGNING_KEY_MESSAGE,
    resolveClientSigningDeviceId,
} from 'wallets/clientSigningDevice'

const Amount = ({ amount, contextStyles }) => {
    const { useUsd, formatSatsAmount } = useBsvDisplay()

    if (useUsd) {
        return (
            <span className={cn(contextStyles, styles.usdAmountValue)}>
                {formatSatsAmount(amount, { includeUnit: false, compactSats: false })}
            </span>
        )
    }

    const formatAmount = num => {
        return Math.abs(num)
            .toString()
            .split('')
            .reverse()
            .reduce((acc, digit, index) => {
                // Add span with margin every 3 digits, except at the start
                if (index > 0 && index % 3 === 0) {
                    return [...acc, <span key={index} className={styles.digitSeparator} />, digit]
                }
                return [...acc, digit]
            }, [])
            .reverse()
    }

    return <span className={contextStyles}>{formatAmount(amount)}</span>
}

const WalletTabs = ({ activeTab, setActiveTab }) => {
    return (
        <div className={styles.tabs}>
            <button
                className={cn(styles.tab, styles.allTab, activeTab === 'all' && styles.active)}
                onClick={() => setActiveTab('all')}
            >
                All
            </button>
            <button
                className={cn(
                    styles.tab,
                    styles.receivedTab,
                    activeTab === 'received' && styles.active,
                )}
                onClick={() => setActiveTab('received')}
            >
                Received
            </button>
            <button
                className={cn(styles.tab, styles.sentTab, activeTab === 'sent' && styles.active)}
                onClick={() => setActiveTab('sent')}
            >
                Given
            </button>
            <button
                className={cn(styles.tab, styles.boostTab, activeTab === 'boost' && styles.active)}
                onClick={() => setActiveTab('boost')}
            >
                Boosted
            </button>
            <button
                className={cn(styles.tab, styles.feesTab, activeTab === 'fees' && styles.active)}
                onClick={() => setActiveTab('fees')}
            >
                AI Usage
            </button>
            <button
                className={cn(
                    styles.tab,
                    styles.paymentsTab,
                    activeTab === 'payments' && styles.active,
                )}
                onClick={() => setActiveTab('payments')}
            >
                Payments
            </button>
        </div>
    )
}

const WalletTotal = ({ total, activeTab }) => {
    const { useUsd } = useBsvDisplay()

    return (
        <div
            className={cn(styles.total, {
                [styles.allTotal]: activeTab === 'all' && total === 0,
                [styles.receivedTotal]:
                    (activeTab === 'all' && total > 0) || activeTab === 'received',
                [styles.sentTotal]: (activeTab === 'all' && total < 0) || activeTab === 'sent',
                [styles.boostTotal]: activeTab === 'boost',
                [styles.paymentsTotal]: activeTab === 'payments',
                [styles.feesTotal]: activeTab === 'fees',
            })}
        >
            <span className={styles.totalLabel}>
                {activeTab === 'all'
                    ? total === 0
                        ? 'Net'
                        : total > 0
                        ? 'Net Creator'
                        : 'Net Giver'
                    : activeTab === 'received'
                    ? 'Total Received'
                    : activeTab === 'boost'
                    ? 'Total Boosted'
                    : activeTab === 'payments'
                    ? 'Total Payments'
                    : activeTab === 'fees'
                    ? 'Total Fees'
                    : 'Total Given'}
            </span>
            <div className={styles.totalAmount}>
                {!useUsd && (
                    <SatsIcon
                        showBars={true}
                        contextStyles={cn(styles.satsIcon, styles.totalSatsIcon, {
                            [styles.sentSatsIcon]:
                                (activeTab === 'all' && total < 0) || activeTab === 'sent',
                            [styles.receivedSatsIcon]:
                                (activeTab === 'all' && total > 0) || activeTab === 'received',
                            [styles.boostSatsIcon]: activeTab === 'boost',
                            [styles.feesSatsIcon]: activeTab === 'fees',
                            [styles.allSatsIcon]: activeTab === 'all' && total === 0,
                        })}
                    />
                )}
                <Amount amount={total} contextStyles={undefined} />
            </div>
        </div>
    )
}

const Time = ({ date }) => {
    const getTimeAgo = date => {
        const seconds = Math.floor((new Date().getTime() - new Date(date).getTime()) / 1000)

        const intervals = [
            { label: 'y', seconds: 31536000 },
            { label: 'w', seconds: 604800 },
            { label: 'd', seconds: 86400 },
            { label: 'h', seconds: 3600 },
            { label: 'm', seconds: 60 },
            { label: 's', seconds: 1 },
        ]

        for (let interval of intervals) {
            const value = Math.floor(seconds / interval.seconds)
            if (value >= 1) {
                return `${value}${interval.label}`
            }
        }

        return 'now'
    }

    return <span>{getTimeAgo(date)}</span>
}

// Add this helper function to determine the time group
const getTimeGroup = date => {
    const seconds = Math.floor((new Date().getTime() - new Date(date).getTime()) / 1000)

    if (seconds < 86400) return 'today' // < 1 day
    if (seconds < 604800) return 'this_week' // < 1 week
    if (seconds < 2592000) return 'this_month' // < 1 month
    if (seconds < 31536000) return 'this_year' // < 1 year
    return 'older'
}

// Add this component for the group label
const TimeGroupLabel = ({ group }) => {
    const labels = {
        today: 'Today',
        this_week: 'This Week',
        this_month: 'This Month',
        this_year: 'This Year',
        older: 'Older',
    }

    return <div className={styles.timeGroupLabel}>{labels[group]}</div>
}

function WalletHistory() {
    const currentUser = gon?.currentUser
    const [activeTab, setActiveTab] = useState('all')
    const streamFilters = useStreamFilters(undefined, { append: true })
    const queryClient = useQueryClient()
    const [cancellingPayment, setCancellingPayment] = useState(null)
    const modalActions = useStore(state => state.actions.modalActions)
    const { useUsd } = useBsvDisplay()

    // Refresh data based on active tab
    useEffect(() => {
        // Always refresh totals when tab changes
        queryClient.invalidateQueries({ queryKey: ['bsv-upvalue-total', currentUser?.id] })

        // Refresh specific data based on active tab
        switch (activeTab) {
            case 'fees':
                queryClient.invalidateQueries({ queryKey: ['bsv-fee-charges', currentUser?.id] })
                break
            case 'all':
            case 'sent':
            case 'received':
            case 'boost':
                queryClient.invalidateQueries({
                    queryKey: ['bsv-upvalue-history', currentUser?.id],
                })
                break
        }
    }, [activeTab, queryClient, currentUser?.id])

    const { data: history = [], isLoading: historyLoading } = useQuery({
        queryKey: ['bsv-upvalue-history', currentUser?.id],
        queryFn: async () => {
            if (!currentUser?.id) return []
            const upvalues = await api.getBsvUpvalueHistory(currentUser.id)
            return upvalues || []
        },
        enabled: !!currentUser?.id,
        staleTime: 0,
    })

    const { data: totalData, isLoading: totalLoading } = useQuery({
        queryKey: ['bsv-upvalue-total', currentUser?.id],
        queryFn: async () => {
            if (!currentUser?.id) return { total: 0, received: 0, given: 0, boost: 0 }
            const result = await api.getBsvUpvalueTotal(currentUser.id)
            return result || { total: 0, received: 0, given: 0, boost: 0 }
        },
        enabled: !!currentUser?.id,
        staleTime: 0, // Always fetch fresh data
    })

    const { data: feeCharges = [], isLoading: feeChargesLoading } = useQuery({
        queryKey: ['bsv-fee-charges', currentUser?.id],
        queryFn: async () => {
            if (!currentUser?.id) return []
            const fees = await api.getBsvFeeCharges()
            return fees || []
        },
        enabled: !!currentUser?.id,
        staleTime: 0, // Always fetch fresh data
    })

    const { data: payments = [], isLoading: paymentsLoading } = useQuery({
        queryKey: ['bsv-payments', currentUser?.id],
        queryFn: async () => {
            if (!currentUser?.id) return []
            const result = await api.getBsvPayments()
            return result || []
        },
        enabled: !!currentUser?.id,
        staleTime: 0, // Always fetch fresh data
    })

    const handleItemClick = item => {
        if (item.answer?.child_quests?.[0]?.id) {
            streamFilters.selectNewFilter({ questId: item.answer.child_quests[0].id })
        } else if (item.answer_id) {
            streamFilters.selectNewFilter({ questId: item.quest_id, answerId: item.answer_id })
        }
    }

    const handleCancelPayment = (payment, ev) => {
        ev.stopPropagation()

        // First show confirmation modal
        modalActions.openConfirmModal(
            "Cancel this payment? (We'll check if it's safe to cancel first)",
            'Cancel Payment',
            async () => {
                // Don't close modal yet - show loading state instead
                modalActions.openConfirmModal('Checking blockchain status...', '', () => {}, {
                    iconType: 'warning',
                    hideCancel: true,
                    confirmLabel: 'Please wait...',
                })

                setCancellingPayment(payment.id)
                try {
                    const result = await api.cancelBsvPayment(payment.id)
                    if (result.success) {
                        // Show success modal
                        modalActions.openConfirmModal(
                            result.pending
                                ? 'Cancellation requested. We will check whether the payment can still be stopped.'
                                : 'Payment cancelled successfully',
                            'OK',
                            () => {},
                            { iconType: 'success', hideCancel: true },
                        )
                        // Refetch to remove cancelled payment from list
                        queryClient.invalidateQueries({
                            queryKey: ['bsv-payments', currentUser?.id],
                        })
                    } else {
                        // Show error modal
                        modalActions.openConfirmModal(
                            result.error || 'Transaction already on blockchain, cannot cancel',
                            'OK',
                            () => {},
                            { iconType: 'error', hideCancel: true },
                        )
                    }
                } catch (error) {
                    // Network error
                    modalActions.openConfirmModal(
                        'Network error while cancelling payment',
                        'OK',
                        () => {},
                        { iconType: 'error', hideCancel: true },
                    )
                } finally {
                    setCancellingPayment(null)
                }
            },
            { iconType: 'warning' },
        )
    }

    const handleRetryPayment = (payment, ev) => {
        ev.stopPropagation()

        modalActions.openConfirmModal(
            "Retry sending this payment? We'll attempt to send it again using your current available funds.",
            'Retry Payment',
            async () => {
                // Show loading state
                modalActions.openConfirmModal('Retrying payment...', '', () => {}, {
                    iconType: 'warning',
                    hideCancel: true,
                    confirmLabel: 'Please wait...',
                })

                let deviceId
                try {
                    // Self-custody payments are signed on the device that taps Retry.
                    deviceId = await resolveClientSigningDeviceId({
                        userId: currentUser?.id,
                        features: currentUser?.features,
                        getClientShare,
                        serverFallback: treechatSignsWithoutLocalKey(),
                    })
                } catch (error) {
                    modalActions.openConfirmModal(
                        error?.message || NO_LOCAL_SIGNING_KEY_MESSAGE,
                        'OK',
                        () => {},
                        { iconType: 'error', hideCancel: true },
                    )
                    return
                }

                try {
                    const result = await api.retryBsvPayment(payment.id, deviceId)
                    if (result.success) {
                        modalActions.openConfirmModal(
                            'Payment retry initiated. Check back in a few moments.',
                            'OK',
                            () => {},
                            { iconType: 'success', hideCancel: true },
                        )
                        queryClient.invalidateQueries({
                            queryKey: ['bsv-payments', currentUser?.id],
                        })
                    } else {
                        modalActions.openConfirmModal(
                            result.error || 'Failed to retry payment',
                            'OK',
                            () => {},
                            { iconType: 'error', hideCancel: true },
                        )
                    }
                } catch (error) {
                    modalActions.openConfirmModal(
                        'Network error while retrying payment',
                        'OK',
                        () => {},
                        { iconType: 'error', hideCancel: true },
                    )
                }
            },
            { iconType: 'warning' },
        )
    }

    const canCancelPayment = payment => {
        // Can cancel if: no tx_id yet AND status is pending/started/broadcasting
        const cancellableStatuses = ['started', 'broadcasting', 'pending']
        return !payment.tx_id && cancellableStatuses.includes(payment.status)
    }

    const getPaymentDisplayState = payment => {
        // Override: If cancellable, always show the spinner (PENDING)
        // This makes it clear the transaction is actively processing when cancel button appears
        if (canCancelPayment(payment)) {
            return BSV_TX_STATES.PENDING
        }
        // Otherwise use normal state determination logic
        return getBsvTransactionState(payment, payment.tx_id)
    }

    const getTotal = () => {
        if (!totalData) return 0

        let result = 0
        if (activeTab === 'all') result = totalData.received - totalData.given - totalData.boost
        else if (activeTab === 'boost') result = totalData.boost
        else if (activeTab === 'received') result = totalData.received
        else if (activeTab === 'sent') result = totalData.given
        else if (activeTab === 'payments') {
            // "Total Payments" = amount the user has sent (exclude received rows).
            result = payments.reduce(
                (sum, payment) =>
                    sum + (payment.direction === 'received' ? 0 : payment.amount_sats),
                0,
            )
        } else if (activeTab === 'fees') {
            // calculate total fees from fee charges
            result = feeCharges.reduce((sum, fee) => sum + fee.bsv_cost_sats, 0)
        }

        return result
    }

    const filteredHistory = history.filter(item =>
        upvalueMatchesWalletTab(item, activeTab, currentUser.id),
    )

    // when on the fees or payments tab, use respective data instead of history
    const displayData =
        activeTab === 'payments'
            ? [...payments].sort(
                  (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
              )
            : activeTab === 'fees'
            ? [...feeCharges].sort(
                  (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
              )
            : filteredHistory

    // Don't hide the whole panel (and its Payments tab) for users who only have payments.
    if (!history.length && !feeCharges.length && !payments.length) {
        return (
            <div className={styles.walletHistory}>
                <h3 className={styles.title}>Wallet History</h3>
                <div className={styles.empty}>No upvalues yet</div>
            </div>
        )
    }

    const total = getTotal()

    return (
        <div className={styles.walletHistory}>
            <div className={styles.header}>
                <div className={styles.titleContainer}>
                    <div className={styles.title}>Upvalues</div>
                </div>
                <WalletTotal total={total} activeTab={activeTab} />
                <WalletTabs activeTab={activeTab} setActiveTab={setActiveTab} />
            </div>

            <div className={styles.historyList}>
                <AnimatePresence initial={false}>
                    {Object.entries(
                        displayData.reduce((groups, item) => {
                            const group = getTimeGroup(item.created_at)
                            if (!groups[group]) groups[group] = []
                            groups[group].push(item)
                            return groups
                        }, {}),
                    ).map(([group, items]) => (
                        <motion.div
                            key={group}
                            className={styles.timeGroup}
                            initial={{ opacity: 0, y: -20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 20 }}
                            transition={{ duration: 0.3 }}
                        >
                            <TimeGroupLabel group={group} />
                            <AnimatePresence mode="popLayout">
                                {items.map(item => {
                                    // Handle payments differently
                                    if (activeTab === 'payments') {
                                        const isReceived = item.direction === 'received'
                                        return (
                                            <motion.div
                                                key={item.id}
                                                layout
                                                className={cn(styles.historyItem, styles.payments, {
                                                    [styles.received]: isReceived,
                                                })}
                                                initial={{ opacity: 0, y: -20 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                exit={{ opacity: 0, y: 20 }}
                                                transition={{ duration: 0.3 }}
                                                onClick={() =>
                                                    handleItemClick({ ...item, type: 'payment' })
                                                }
                                                data-bsv-tx-uuid={item?.id ?? 'NOT_FOUND'}
                                                data-tx-id={item?.tx_id ?? 'NOT_FOUND'}
                                            >
                                                <div className={styles.timestamp}>
                                                    <Time date={item.created_at} />
                                                </div>
                                                <div className={cn(styles.amount, styles.payments)}>
                                                    {!useUsd && (
                                                        <SatsIcon
                                                            showBars={true}
                                                            contextStyles={cn(
                                                                styles.satsIcon,
                                                                styles.paymentsSatsIcon,
                                                            )}
                                                        />
                                                    )}
                                                    <Amount
                                                        amount={item.amount_sats}
                                                        contextStyles={undefined}
                                                    />
                                                    <BsvTransactionStatusIcon
                                                        bsvTx={item}
                                                        fallbackTxId={item.tx_id}
                                                        iconClassName={styles.paymentTxLink}
                                                        iconClassNameMap={{
                                                            failed: styles.paymentTxFailed,
                                                        }}
                                                        overrideState={getPaymentDisplayState(item)}
                                                    />
                                                    {!isReceived && canCancelPayment(item) && (
                                                        <WithTooltip text="Cancel payment">
                                                            <button
                                                                className={cn(
                                                                    styles.cancelButton,
                                                                    cancellingPayment === item.id &&
                                                                        styles.cancelling,
                                                                )}
                                                                onClick={ev =>
                                                                    handleCancelPayment(item, ev)
                                                                }
                                                                disabled={
                                                                    cancellingPayment === item.id
                                                                }
                                                            >
                                                                <X size={16} />
                                                            </button>
                                                        </WithTooltip>
                                                    )}
                                                    {!isReceived &&
                                                        item.to_address &&
                                                        ((item.status === 'failed' &&
                                                            item.retries >= item.max_retries) ||
                                                            item.status === 'cancelled') && (
                                                            <WithTooltip text="Retry payment">
                                                                <button
                                                                    className={styles.retryButton}
                                                                    onClick={ev =>
                                                                        handleRetryPayment(item, ev)
                                                                    }
                                                                >
                                                                    <RotateCw size={16} />
                                                                </button>
                                                            </WithTooltip>
                                                        )}
                                                </div>
                                                {isReceived ? (
                                                    <div className={styles.userInfo}>
                                                        <span className={styles.userLabel}>
                                                            FROM
                                                        </span>
                                                        <span className={styles.username}>
                                                            {item.counterparty_name
                                                                ? `@${item.counterparty_name}`
                                                                : 'sender'}
                                                        </span>
                                                    </div>
                                                ) : (
                                                    <div className={styles.userInfo}>
                                                        <span className={styles.userLabel}>TO</span>
                                                        {/* An HTML ordinal's payment pays scripts, not an address. */}
                                                        {item.to_address ? (
                                                            <a
                                                                href={`https://whatsonchain.com/address/${item.to_address}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className={styles.address}
                                                                title={item.to_address}
                                                                onClick={ev => {
                                                                    ev.stopPropagation()
                                                                }}
                                                            >
                                                                {item.counterparty_name
                                                                    ? `@${item.counterparty_name}`
                                                                    : `${item.to_address.substring(
                                                                          0,
                                                                          8,
                                                                      )}...${item.to_address.substring(
                                                                          item.to_address.length -
                                                                              6,
                                                                      )}`}
                                                            </a>
                                                        ) : (
                                                            <span className={styles.address}>
                                                                Ordinal payment
                                                            </span>
                                                        )}
                                                    </div>
                                                )}
                                            </motion.div>
                                        )
                                    }

                                    // Handle fee charges differently
                                    if (activeTab === 'fees') {
                                        return (
                                            <motion.div
                                                key={item.id}
                                                layout
                                                className={cn(styles.historyItem, styles.fees)}
                                                initial={{ opacity: 0, y: -20 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                exit={{ opacity: 0, y: 20 }}
                                                transition={{ duration: 0.3 }}
                                                onClick={() =>
                                                    handleItemClick({ ...item, type: 'fee' })
                                                }
                                                data-fee-charge-id={item?.id ?? 'NOT_FOUND'}
                                                data-tx-id={item?.bsv_tx_id ?? 'NOT_FOUND'}
                                            >
                                                <div className={styles.timestamp}>
                                                    <Time date={item.created_at} />
                                                </div>
                                                <div className={cn(styles.amount, styles.fees)}>
                                                    {!useUsd && (
                                                        <SatsIcon
                                                            showBars={true}
                                                            contextStyles={cn(
                                                                styles.satsIcon,
                                                                styles.feesSatsIcon,
                                                            )}
                                                        />
                                                    )}
                                                    <Amount
                                                        amount={item.bsv_cost_sats}
                                                        contextStyles={undefined}
                                                    />
                                                    {item.bsv_tx_id && (
                                                        <BsvTransactionStatusIcon
                                                            fallbackTxId={item.bsv_tx_id}
                                                            iconClassName={styles.feeTxLink}
                                                        />
                                                    )}
                                                </div>
                                                <div className={styles.userInfo}>
                                                    <span className={styles.userLabel}>FOR</span>
                                                    <span className={styles.username}>
                                                        {item.ai_model?.name || 'AI Model'}
                                                    </span>
                                                </div>
                                            </motion.div>
                                        )
                                    }

                                    // handle other types
                                    const transactionType =
                                        currentUser.id === item.from_user_id ? 'Sent' : 'Received'
                                    const otherUser =
                                        transactionType === 'Sent' ? item.to_user : item.from_user
                                    const isBoost = item.is_boost

                                    return (
                                        <motion.div
                                            key={item.id}
                                            layout
                                            className={cn(styles.historyItem, {
                                                [styles.sent]:
                                                    transactionType === 'Sent' && !isBoost,
                                                [styles.received]:
                                                    transactionType === 'Received' && !isBoost,
                                                [styles.boost]: isBoost,
                                            })}
                                            onClick={() => handleItemClick(item)}
                                            initial={{ opacity: 0, y: -20 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, y: 20 }}
                                            transition={{ duration: 0.3 }}
                                            data-upvalue-id={item?.id ?? 'NOT_FOUND'}
                                            data-bsv-tx-uuid={item?.bsv_tx?.id ?? 'NOT_FOUND'}
                                            data-tx-id={item?.bsv_tx?.tx_id ?? 'NOT_FOUND'}
                                        >
                                            <div className={styles.timestamp}>
                                                <Time date={item.created_at} />
                                            </div>
                                            <div
                                                className={cn(styles.amount, {
                                                    [styles.sent]:
                                                        transactionType === 'Sent' && !isBoost,
                                                    [styles.received]:
                                                        transactionType === 'Received' && !isBoost,
                                                    [styles.boost]: isBoost,
                                                })}
                                            >
                                                {!useUsd && (
                                                    <SatsIcon
                                                        showBars={true}
                                                        contextStyles={cn(styles.satsIcon, {
                                                            [styles.sentSatsIcon]:
                                                                transactionType === 'Sent' &&
                                                                !isBoost,
                                                            [styles.receivedSatsIcon]:
                                                                transactionType === 'Received' &&
                                                                !isBoost,
                                                            [styles.boostSatsIcon]: isBoost,
                                                        })}
                                                    />
                                                )}
                                                <Amount
                                                    amount={Math.floor(upvalueDisplayAmount(item))}
                                                    contextStyles={undefined}
                                                />
                                                {item.bsv_tx && (
                                                    <BsvTransactionStatusIcon
                                                        bsvTx={item.bsv_tx}
                                                        iconClassName={styles.upvalueTxLink}
                                                    />
                                                )}
                                            </div>
                                            {otherUser && (
                                                <div className={styles.userInfo}>
                                                    <span className={styles.userLabel}>
                                                        {transactionType === 'Sent' ? 'TO' : 'FROM'}
                                                    </span>
                                                    <UserStream
                                                        user={otherUser}
                                                        contextStyles={styles.userIcon}
                                                        disable={false}
                                                        size={'small'}
                                                    />
                                                    <span className={styles.username}>
                                                        {otherUser.name}
                                                    </span>
                                                </div>
                                            )}
                                        </motion.div>
                                    )
                                })}
                            </AnimatePresence>
                        </motion.div>
                    ))}
                </AnimatePresence>
            </div>
        </div>
    )
}

export default function WalletHistoryWrapper() {
    return (
        <ErrorBoundary label="WalletHistory">
            <WalletHistory />
        </ErrorBoundary>
    )
}
